import AuthMixin from '@/components/mixins/auth-mixin'
import EventBus from '@/components/mixins/event-bus-mixin'
import CommonOptionsMixin from '@/components/mixins/common-options-mixin'
import OnEnterMixin from '@/components/mixins/onenter-mixin'
import MediaMenu from '@/components/common/media-menu/media-menu-tmpl'
import LedResolutionMenu from '@/components/common/led-resolution-menu/led-resolution-menu-tmpl'
import LedTagSlider from '@/components/common/led-tag-slider/led-tag-slider-tmpl'
import MediaSearchBar from '@/components/common/media-search-bar/media-search-bar-tmpl'
import TagSlider from '@/components/common/tag-slider/tag-slider-tmpl'
import PopIdentifierSlider from '@/components/common/pop-identifier-slider/pop-identifier-slider-tmpl'
import AudioPlayer from '@/components/common/audio-player/audio-player-tmpl'
import DurationInput from '@/components/common/duration-input/duration-input-tmpl'
import ImagePreviewDialog from '@/components/common/image-preview-dialog/image-preview-dialog-tmpl'
import VideoPlayerDialog from '@/components/common/video-player-dialog/video-player-dialog-tmpl'
import UnsavedDataMixin from '@/components/mixins/unsaved-data-mixin'
import BezelsMask from '../bezels-mask/bezels-mask-tmpl'
import Draggable from 'vuedraggable'
import { hasAccessToLEDPage, hasPermissionToCreateLEDProject, hasPermissionToCreateLEDResolutionTag } from '@/utils/led-page-accessibility.js'
import { hasAccessToVideowallPage, hasPermissionToCreateVideowallProject } from '@/utils/videowall-page-accessibility.js'
import Canvas, { FindPlayerSchedulesByLEDTag } from '@/components/pages/player/player-setup/player-data-canvas'
import CONST from '@/globals/constants'
import _ from 'lodash'
import { mapActions, mapState as piniaMapState } from 'pinia'
import { tagsStore } from '@/pinia/stores/tagsStore'
import { scheduleStore } from '@/pinia/stores/schedule/scheduleStore'
import { useFileUploadStorage } from '@/composables/file-upload/use-file-upload-storage'
import { useFileUploaderStore } from '@/pinia/stores/file-upload/fileUploaderStore'
import { useMixpanel } from '@base-shared/composables/use-mixpanel'
import { useCognitoAuthStore } from '@base-shared/stores/cognito-auth'

import { FwbButton, FwbBadge } from 'flowbite-vue';

const MEDIA_DEFAULT_COLUMNS_HORIZONTAL = 3
const MEDIA_DEFAULT_COLUMNS_VERTICAL = 4
const MEDIA_DEFAULT_GUTTER = 20

const MEDIA_COPY_PASTE_TEXTAREA_ID = 'mediaOnHoverCopyPasteContainer'
const MEDIA_FILTERS_DATA = {}
MEDIA_FILTERS_DATA[CONST.FILTER_KEY_ORIENTATION] = ''
MEDIA_FILTERS_DATA[CONST.FILTER_KEY_SEARCH_TEXT] = ''
MEDIA_FILTERS_DATA[CONST.FILTER_KEY_MEDIA_TYPES] = []
MEDIA_FILTERS_DATA[CONST.FILTER_KEY_POPIDS] = []
MEDIA_FILTERS_DATA[CONST.FILTER_KEY_MEDIA_TAGS] = []
MEDIA_FILTERS_DATA[CONST.FILTER_KEY_LED_TAGS] = []
MEDIA_FILTERS_DATA[CONST.FILTER_KEY_VIDEOWALL_TAGS] = []

export default {
  name: 'media',
  inject: [
    '$currentUser',
    '$utils',
    '$dataStore',
    '$api',
    '$apiError',
    '$filtersRepository'
  ],
  components: {
    MediaMenu,
    MediaSearchBar,
    TagSlider,
    PopIdentifierSlider,
    LedTagSlider,
    AudioPlayer,
    DurationInput,
    ImagePreviewDialog,
    VideoPlayerDialog,
    LedResolutionMenu,
    BezelsMask,
    Draggable,
    FwbButton,
    FwbBadge
  },
  props: {
    isParentComponentMountedProp: {
      default () { return false }
    },
    mediaComponentAsProp: {
      default () { return CONST.MEDIA_COMPONENT_AS_MEDIA_FILES }
    },
    isActive: {
      type: Boolean,
      default: true
    }
  },
  computed: {
    sortDraggableOptions () {
      return { animation: 250 }
    },
    ...piniaMapState(useFileUploaderStore, {
      fileUploaderState: 'uploadedFiles'
    }),
    isFileUploadButtonVisible () {
      return this.enableMediaUpload && _.isEmpty(this.mediaSelection) && !this.confirmForceToDeleteMedia.showDialog
    },
    isNewLEDButtonVisible () {
      // NOTE: if there's no permission to create, then there's no permission to create new resolutions either
      // `this.enableNewLed` is a proxy state of hasPermissionToCreateLEDResolutionTag
      if (!hasPermissionToCreateLEDResolutionTag(this.$currentUser) && !this.enableNewLed) {
        return false
      }
      return this.enableNewLed && this.isMediaComponentAsLed && !this.isLedResolutionMenuVisible && _.isEmpty(this.mediaSelection)
    },
    isLEDFormVisible () {
      return this.isMediaComponentAsLed && this.isLedResolutionMenuVisible && _.isEmpty(this.mediaSelection)
    },
    isNewVideowallButtonVisible () {
      // NOTE: if there's no permission to create, then there's no permission to create new resolutions either
      if (!hasPermissionToCreateVideowallProject(this.$currentUser)) {
        return false
      }
      return this.isMediaComponentAsVideowall && !this.isVideowallResolutionMenuVisible && _.isEmpty(this.mediaSelection)
    },
    isVideowallFormVisible () {
      return this.isMediaComponentAsVideowall && this.isVideowallResolutionMenuVisible && _.isEmpty(this.mediaSelection)
    },
    isMediaSettingsVisible () {
      return !_.isEmpty(this.mediaSelection) || this.confirmForceToDeleteMedia.showDialog
    },
    isSelectAllMediaButtonVisible () {
      return (_.isEmpty(this.mediaSelection) || this.confirmForceToDeleteMedia.showDialog) && (!_.isEmpty(this.mediaList.itemsToShow) && this.$currentUser.hasGroupDevelopment())
    },
    ...piniaMapState(tagsStore, {
      tagsStoreResourceTags: 'resourceTags',
      tagsStoreLedTags: 'ledTags',
      tagsStoreVideowallTags: 'videowallTags',
      tagsStorePlayerTags: 'playerTags',
      tagsStoreFetchErrors: 'error',
    }),
    ...piniaMapState(scheduleStore, {
      scheduleStoreIsFetching: 'isFetching'
    }),

    /**
     * Determines whether the toggle to Media V2 should be
     * shown in the UI.
     */
    showToggleToMediaV2 () {
      return !this.isLEDFormVisible && !this.isVideowallFormVisible && !this.isMediaSettingsVisible
    }
  },
  watch: {
    scheduleStoreIsFetching (newVal) {
      this.$utils.mainLoader(newVal)
    },
    fileUploaderState (newVal) {
      this.handleNewUploadedFiles(newVal)
    },
    isParentComponentMountedProp (newVal) {
      this.isParentComponentMounted = newVal
    },
    mediaComponentAsProp (newVal, prevVal) {
      this.mediaComponentAs = newVal
      this.setDefaultData({ prevAction: 'mediaComponentUsageChanged' })
    }
  },
  setup () {
    const fileUploadStorage = useFileUploadStorage()
    const fileUploaderStore = useFileUploaderStore();

    return { fileUploadStorage, fileUploaderStore }
  },
  data () {
    return {
      CONST,
      mediaFilters: MEDIA_FILTERS_DATA,
      isUserCreateLocked: this.$currentUser.getRights().userCreateLock,
      enableMediaUpload: false,
      enableDeleteMedia: this.$currentUser.getRights().userCreate.deleteMedia,
      enableEditMediaName: this.$currentUser.getRights().userCreate.editMediaName,
      enableCreateNewProject: this.$currentUser.getRights().userCreate.createNewProject,
      enableEditProject: this.$currentUser.getRights().userCreate.editProject,
      enableCopyMedia: this.$currentUser.getRights().userCreate.editProject,
      enableNewLed: hasPermissionToCreateLEDResolutionTag(this.$currentUser),
      isCovidButtonVisible: false,
      isOrientationFilterVisible: false,
      isLedResolutionMenuVisible: false,
      isVideowallResolutionMenuVisible: false,
      isLedTagsReady: false,
      isResolutionCalculatorVisible: false,
      error: '',
      isFetchingMedia: false,
      fetchingMediaSelectionDetails: false,
      inlineEditName: {
        isInputVisible: false,
        mediaResourceId: null,
        previousVal: null,
        requesting: false
      },
      isMediaComponentAsMediaFiles: true,
      isMediaComponentAsAlert: false,
      isMediaComponentAsLed: false,
      isMediaComponentAsVideowall: false,
      mediaComponentAs: this.mediaComponentAsProp,
      mediaList: {
        batchHistory: [],
        itemsToShow: [],
        nextFetch: {
          moreItems: false,
          exclusiveStartResourceId: null
        },
        reqFilter: { type: [], search: '' }
      },
      ledTags: [],
      videowallBezels: {},
      mediaOrientation: CONST.ORIENTATION_VALUE_HORIZONTAL,
      mediaSelection: [],
      previousMediaItemSelected: {
        mediaItem: {},
        mediaItemPosition: 0
      },
      previousKeyPressedInMediaSelection: null,
      userSchedules: [],
      userSchedulesFilteredByResolution: [],
      imagePreview: {
        dialog: false,
        image: null
      },
      playVideo: {
        dialog: false,
        videopreviews: []
      },
      playAudio: {
        audio: null,
        isPlaying: false
      },
      updateAudioPlayer: null,
      isParentComponentMounted: this.isParentComponentMountedProp,
      newMediaUploadFileList: [],
      confirmForceToDeleteMedia: {
        showDialog: false,
        items: [],
        confirmed: false
      },
      mediaMenuTriggerPropertyAction: {},
      allMediaItemActionIconHover: false,
      hideMediaMenuComponentTemplate: false,
      selectedTagsFromSlider: [],
      selectedPopIdsFromSlider: [],
      refreshTagSlider: { ref: null, refresh: false },
      refreshPopIdsSlider: { ref: null, refresh: false },
      tagsSelectionRequest: { ref: null, tags: [], default: false },
      tagsSummaryFromTagsSlider: [],
      popIdsSummaryFromTagsSlider: [],
      tagsSummaryForSuggestions: [],
      listOfSvgToUpdateOnLoad: [],
      showCardActions: false,
      isCtrlKeyPressed: false,
      isShiftKeyPressed: false,
      hoveredItem: null,
      showPopIdsSliderFilter: this.$currentUser.hasFeatureProofOfPlayExtended(),
      refilterLedTags: { ref: null, prevFilterAction: '' },
      languageCode: navigator?.language ?? 'en'
    }
  },
  mixins: [AuthMixin, CommonOptionsMixin, UnsavedDataMixin, OnEnterMixin],
  methods: {
    ...mapActions(tagsStore, ['fetchTags']),
    ...mapActions(scheduleStore, ['fetchAllSchedules']),
    setDefaultData (params = {}) {
      // Set where is media component used for
      this.isMediaComponentAsAlert = this.mediaComponentAsProp === CONST.MEDIA_COMPONENT_AS_ALERT_FILES
      this.isMediaComponentAsLed = this.mediaComponentAsProp === CONST.MEDIA_COMPONENT_AS_LED_FILES
      this.isMediaComponentAsVideowall = this.mediaComponentAsProp === CONST.MEDIA_COMPONENT_AS_VIDEOWALL_FILES
      this.isMediaComponentAsMediaFiles = !this.isMediaComponentAsAlert && !this.isMediaComponentAsLed && !this.isMediaComponentAsVideowall

      this.setMediaFiltersFromRepository() // Set default media filters data
      this.addMessageListener()
      this.ctrlAndShiftKeyPressedWatcher()
      this.windowClickedWatcher()
      this.mediaOrientation = this.$dataStore.getMediaOrientation()

      $(window).on('resize', () => {
        this.$utils.updatePageComponentStyle({ name: (this.isMediaComponentAsVideowall || this.isMediaComponentAsLed) ? 'ledPage' : 'mediaFiles' })
      })

      // re-render list on resize
      $(window).on('resize', _.debounce(this.rerenderMediaList, 50))

      /** Set Media card action icons visibility for MEDIA FILES, LED and VIDEOWALL PAGE */
      if ((this.isMediaComponentAsMediaFiles || this.isMediaComponentAsVideowall || this.isMediaComponentAsLed) && this.$currentUser.hasGroupExtended()) {
        // Edit Project Icon
        if (this.$currentUser.hasGroupUserLimited()) {
          this.enableCopyMedia = false
          this.enableEditProject = true
          this.enableDeleteMedia = false
        } else if (!this.$currentUser.hasGroupUserAdmin() && !this.$currentUser.hasGroupUserCreate() && !this.$currentUser.hasGroupUserCreateLock()) {
          this.enableCopyMedia = false
          this.enableEditProject = false
          this.enableDeleteMedia = false
        }
      }

      // Reset Media list Data
      this.mediaSelection = []
      const mediaList = this.$utils.clone(this.mediaList)
      mediaList.reqFilter = { type: [], search: '' }
      this.mediaList = mediaList

      // Fetch media list on scroll
      this.$utils.infiniteScroll('mediaItemsContainer', () => {
        if (this.mediaList.nextFetch.moreItems) {
          this.getMediaList({ addRequestParams: { exclusiveStartResourceId: this.mediaList.nextFetch.exclusiveStartResourceId } })
        }
      })

      // Reset inline edit media name
      this.inlineEditName = {
        isInputVisible: false,
        mediaResourceId: null,
        previousVal: null,
        requesting: false
      }

      this.listOfSvgToUpdateOnLoad = []
      this.isResolutionCalculatorVisible = false

      this.isAuthenticated({
        callback: () => {
          this.isFetchingMedia = true
          this.getTagsSummary(() => {
            this.getMediaList({
              resetMediaList: true,
              callback: () => {
                this.getUserSchedules()
                this.handleOrientationFilterVisibility()
                this.handleCardActionsVisibility()
                this.$nextTick(_ => {
                  this.$utils.updatePageComponentStyle({ name: this.isMediaComponentAsLed ? 'ledPage' : 'mediaFiles' })
                })

                // Mixpanel track
                if (this.isMediaComponentAsMediaFiles) {
                  useMixpanel().track('Create - media')
                } else {
                  useMixpanel().track('Create - alert')
                }
              },
              prevAction: params.actionType ? params.actionType : null
            })
          })
        }
      })
    },
    rerenderMediaList () {
      const mediaList = this.$utils.clone(this.mediaList)
      this.mediaList = this.filterMediaList(mediaList)
    },
    isMediaOrientationVertical () {
      return this.mediaFilters.orientation === CONST.ORIENTATION_VALUE_VERTICAL
    },
    setMediaFiltersFromRepository () {
      const mediaFiltersData = this.mediaFilters ?? {}
      mediaFiltersData.orientation = this.$filtersRepository.findOrientation() ?? CONST.ORIENTATION_VALUE_HORIZONTAL
      mediaFiltersData.searchText = this.$filtersRepository.findSearchText()
      mediaFiltersData.mediaTypes = this.$filtersRepository.findMediaTypes()
      mediaFiltersData.mediaTags = this.$filtersRepository.findMediaTags()
      mediaFiltersData.popIds = this.$filtersRepository.findPopIds()
      mediaFiltersData.ledTags = []; // Always clear ledTags here to avoid retaining them when switching between LED and FILES pages

      // Populate ledTags whenever the component is mounted for the LED/Videowall Page only
      if (this.isMediaComponentAsLed) {
        mediaFiltersData.ledTags = this.$filtersRepository.findLedTags()
      }

      if (this.isMediaComponentAsVideowall) {
        mediaFiltersData.ledTags = this.$filtersRepository.findVideowallTags()
      }

      this.mediaFilters = mediaFiltersData
    },
    setMediaOrientationFilter (orientation = '') {
      this.mediaFilters.orientation = orientation
      this.$filtersRepository.saveOrientation(orientation)
      this.$nextTick(this.rerenderMediaList)
    },
    handleOrientationIconClick (orientation = CONST.ORIENTATION_VALUE_HORIZONTAL) {
      this.setMediaOrientationFilter(orientation)
      this.getMediaList()
    },
    handleCardActionsVisibility () {
      this.showCardActions = true
    },
    handleOrientationFilterVisibility () {
      this.isOrientationFilterVisible = !this.$currentUser.hasGroupUserLimited() && !this.isMediaComponentAsLed && !this.isMediaComponentAsVideowall
    },
    handleClientSearch () {
      const mediaList = this.mediaList
      const mediaByBatch = this.mediaList.batchHistory
      let newResult = _.cloneDeep(_.flatten(mediaByBatch))
      let hasMatch = false
      const searchText = this.mediaFilters.searchText
      const selectedMediaTypes = this.mediaFilters.mediaTypes
      const selectedMediaTags = this.mediaFilters.mediaTags
      const selectedPopids = this.mediaFilters.popIds

      if (!_.isEmpty(searchText)) {
        const stResult = newResult.filter(m => m.name.toLowerCase().includes(searchText.toLowerCase()))

        if (!_.isEmpty(stResult)) {
          newResult = stResult
          hasMatch = true
        }
      }

      if (!_.isEmpty(selectedMediaTypes)) {
        const smTypesResult = newResult.filter(m => selectedMediaTypes.includes(m.type))

        if (!_.isEmpty(smTypesResult)) {
          newResult = smTypesResult
          hasMatch = true
        }
      }

      if (!_.isEmpty(selectedMediaTags)) {
        const smTagsResult = newResult.filter(m => m.tags.sort().join(',') === selectedMediaTags.sort().join(','))

        if (!_.isEmpty(smTagsResult)) {
          newResult = smTagsResult
          hasMatch = true
        }
      }

      if (!_.isEmpty(selectedPopids)) {
        const sPopIdsResult = newResult.filter(m => selectedPopids.includes(m.popId))

        if (!_.isEmpty(sPopIdsResult)) {
          newResult = sPopIdsResult
          hasMatch = true
        }
      }

      mediaList.itemsToShow = newResult
      if (!hasMatch) {
        mediaList.itemsToShow = []
      }

      this.mediaList = this.filterMediaList(mediaList)

      // Try to fetch some media from the backend
      this.$utils.mainLoader(false)
      this.getMediaList({
        hideLoader: true,
        callback: (newMediaBatch) => {
          const mediaList = this.mediaList
          mediaList.itemsToShow = mediaList.itemsToShow.concat(newMediaBatch)
          this.mediaList = this.filterMediaList(mediaList)
        }
      })
    },
    handleMediaSearchBarChange ($event = {}) {
      this.setMediaFiltersFromRepository()

      if ($event.action === CONST.EMIT_EVENT_SEARCH_TEXT_CHANGED_ENTER) {
        this.getMediaList({ resetMediaList: true })
      }

      if ($event.action === CONST.EMIT_EVENT_SEARCH_TEXT_CHANGED_TYPING) {
        this.handleClientSearch()
      }

      if ($event.action === CONST.EMIT_EVENT_SELECTED_MEDIA_TYPES_CHANGED) {
        this.getMediaList({ resetMediaList: true })
      }
    },
    handleInlineEditNameClick (event, mediaItem, currentMediaItemPosition) {
      // User Rights contract level
      if (!this.enableEditMediaName) return
      if (mediaItem.disabled) return

      // Check media if selected
      let isMediaSelected = false
      for (const indx in this.mediaSelection) {
        const mediaItemSrc = this.mediaSelection[indx]

        if (mediaItemSrc.resourceId === mediaItem.resourceId) {
          isMediaSelected = true
          break
        }
      }

      // If media is selected, then enable the edit media name field
      if (isMediaSelected) {
        const inlineEditName = this.$utils.clone(this.inlineEditName)
        inlineEditName.isInputVisible = true
        inlineEditName.mediaResourceId = mediaItem.resourceId

        this.inlineEditName = inlineEditName
        this.$nextTick(_ => {
          this.$refs[`${mediaItem.resourceId}-editName`][0].$refs.input.focus()
          this.inlineEditName.previousVal = mediaItem.name
        })
      }

      // If media is not selected, then set the media a selected
      if (!isMediaSelected) {
        this.handleMediaCardSelection(event, mediaItem, currentMediaItemPosition)
      }
    },
    handleInlineEditNameInputConfirm (mediaItem, indx) {
      if (this.inlineEditName.previousVal === mediaItem.name || this.inlineEditName.requesting) return

      // Reset modified file temporarily just for optimistic update instead of updating localstorage completely
      const mediaList = this.$utils.clone(this.mediaList)
      if (mediaList.itemsToShow[indx]._edit && mediaList.itemsToShow[indx]._edit.modifiedFile) {
        mediaList.itemsToShow[indx]._edit.modifiedFile = false
        this.mediaList = mediaList
      }

      const requestData = mediaItem
      const pageOrientation = this.$dataStore.getMediaOrientation()
      const width = 424

      const apiRequest = () => {
        const inlineEditName = this.$utils.clone(this.inlineEditName)
        inlineEditName.requesting = true
        this.inlineEditName = inlineEditName

        if (requestData.status) delete requestData.status

        this.$api.put(`${CONFIG.API_BASE_URL}/media/${mediaItem.resourceId}?use_qsa=1&dir=${pageOrientation}&width=${width}`, requestData)
          .then(response => {
            const inlineEditName = this.$utils.clone(this.inlineEditName)
            inlineEditName.isInputVisible = false
            inlineEditName.mediaResourceId = null
            inlineEditName.requesting = false
            this.inlineEditName = inlineEditName
            const updatedMediaItem = response.data

            // Let's update current media list
            const mediaList = this.$utils.clone(this.mediaList)
            mediaList.itemsToShow = this.$utils.updateItemFromList(mediaList.itemsToShow, updatedMediaItem, 'resourceId') // Update media items in Media List
            mediaList.batchHistory.forEach((mediaListByBatch) => { // Update Media List by batch
              mediaListByBatch = this.$utils.updateItemFromList(mediaListByBatch, updatedMediaItem, 'resourceId')
            })
            this.mediaList = this.filterMediaList(mediaList)
          })
          .catch((error) => {
            this.$apiError.handleUnauthorizedError(error, { originalRequest: () => { apiRequest() } })

            const inlineEditName = this.$utils.clone(this.inlineEditName)
            inlineEditName.requesting = false
            this.inlineEditName = inlineEditName
          })
      }
      apiRequest()
    },
    handleLedResolutionMenuChange (event) {
      this.autoSelectNewTagInSlider = { select: false, tags: [] }

      if (event.action === 'NEW_SETTING_CREATED') {
        // redirect to create project
        const dimension = `${event.payload.data.width}x${event.payload.data.height}`
        let path = '/template/default/led'
        const query = {
          dimensions: dimension
        }

        if (event.payload.data.type === 'videowall') {
          query.videowallTags = this.$utils.parseLedTag(event.payload.data.tag).queryStringValue
          path = '/template/default/videowall'
        } else if (event.payload.data.type === 'led') {
          query.ledTags = this.$utils.parseLedTag(event.payload.data.tag).queryStringValue
        }

        this.$router.push({
          path,
          query
        })
      } else if (event.action === 'SHOW_RESOLUTION_CALCULATOR') {
        this.isResolutionCalculatorVisible = event.payload.data?.isVisible ?? false
      }
    },
    handleNewLedConfigClick () {
      this.isLedResolutionMenuVisible = true
    },
    handleNewVideowallConfigClick () {
      this.isVideowallResolutionMenuVisible = true
    },
    /**
     * This is quick and dirty hack to manage correct "gutter"
     * calculation to accomodate even horizontal distribution of cards
     * while not distort card preview width with `padding` style CSS attribute
     * generated by :gutter property of el-col in Element UI.
     *
     * @param {*} mediaItemWidth - width of the card preview
     * @returns { columns: Number, baseWidth: Number} - estimated columns quantity and width of working area
     */
    calculateNumberOfCardsPerRow (mediaItemWidth, minimumMargin = 5) {
      const element = document.getElementById('mediaItemsToShow')
      const style = window.getComputedStyle(element)

      if (style) {
        const width = parseInt(style.width.replace('px', ''), 10)
        const count = Math.floor(width / mediaItemWidth)
        const residuals = width - count * mediaItemWidth
        if (residuals / count < 5) {
          return { columns: count - 1, baseWidth: width }
        }

        return { columns: count, baseWidth: width }
      }

      return { columns: 3, baseWidth: 1024 }
    },
    filterMediaList (mediaListSrc = []) {
      const sessionStage = useCognitoAuthStore().sessionStage ?? CONFIG.GLOBAL_STAGE;
      const APIUrl = this.$utils.getStageAPIUrl(sessionStage)
      const marginBase = MEDIA_DEFAULT_GUTTER
      const columns = this.isMediaOrientationVertical()
        ? MEDIA_DEFAULT_COLUMNS_VERTICAL
        : MEDIA_DEFAULT_COLUMNS_HORIZONTAL

      const mediaList = _.clone(mediaListSrc)
      /**
       * Add NEW item to create project project for LED or Video wall
       * This item must be at the last position of the list
       */
      const isNewLEDProjectVisible = this.isMediaComponentAsLed && hasPermissionToCreateLEDProject(this.$currentUser)
      const isNewVideowallProjectVisible = this.isMediaComponentAsVideowall && hasPermissionToCreateVideowallProject(this.$currentUser)

      if ((isNewLEDProjectVisible || isNewVideowallProjectVisible)) {
        mediaList.itemsToShow = mediaList.itemsToShow.filter(i => i.resourceId !== 'default')
        const selectedLedTag = this.getSelectedLedTag()
        const createProject = {
          width: selectedLedTag.width,
          height: selectedLedTag.height,
          size: `${new Intl.NumberFormat(this.languageCode).format(selectedLedTag.width)} x ${new Intl.NumberFormat(this.languageCode).format(selectedLedTag.height)}`,
          resourceId: 'default',
          newLED: this.isMediaComponentAsLed,
          newVideowall: this.isMediaComponentAsVideowall,
          name: 'NEW',
          type: 'svg',
          preview: {
            type: 'svg',
            url: this.$utils.filterMediaPreviewUrl(`${APIUrl}/template/qsa/default/led/icon.svg`, {
              queryParams: {
                token: this.$currentUser.getJWToken(),
                dimensions: selectedLedTag.dimension,
                videowallTags: this.isMediaComponentAsVideowall ? this.getSelectedLedTag().value : null
              }
            })
          }
        }

        // Add NEW item into the list
        mediaList.itemsToShow.push(createProject)

        // Media must have led or videoWall tag
        mediaList.itemsToShow = mediaList.itemsToShow.filter(media => media.resourceId === 'default' || ((!_.isEmpty(media.led) || !_.isEmpty(media.videowall)) && !_.isEmpty(this.ledTags)))
      }

      // Should have no duplicate media in the list
      mediaList.itemsToShow = _.uniqBy(mediaList.itemsToShow, (media) => media.resourceId)
      let cardsCounter = 0
      const mediaItemsCount = mediaList.itemsToShow.length
      // Add each media item properties for UI
      for (const mediaItem of mediaList.itemsToShow) {
        if (!mediaItem._edit) {
          mediaItem._edit = {
            uid: mediaItem.resourceId,
            selected: false,
            isHover: false,
            isActive: false,
            previewElStyle: ''
          }
        } else {
          mediaItem._edit.selected = false
          mediaItem._edit.isHover = false
        }

        mediaItem._edit.modifiedFile = false

        if (mediaItem.modifiedAt) {
          const file = this.$utils.clone(mediaItem)
          file.type = 'media'
          mediaItem._edit.modifiedFile = this.$dataStore.isModifiedFile({ file })
        }

        // Disabled modifiedFile if media still uploading
        if ((mediaItem.groupId === this.$currentUser.getUserId()) && mediaItem.status) mediaItem._edit.modifiedFile = false

        // Set media item as selected if present from selection data
        mediaItem._edit.selected = !_.isEmpty(this.mediaSelection.find(i => i.resourceId === mediaItem.resourceId))

        // Set media item as isHover to false if not selected
        mediaItem._edit.isHover = mediaItem._edit.selected
        mediaItem._edit.cardStyle = {
          'margin-right': '20px',
          width: 'calc(33.333% - 20px)'
        }

        // Add dimension if led page
        if (this.isMediaComponentAsLed || this.isMediaComponentAsVideowall) {
          // Add led dimension if not present
          mediaItem.width = this.getSelectedLedTag().width
          mediaItem.height = this.getSelectedLedTag().height

          // Calculate aspect ratio from current page content to get media card element dimension
          const calculateSizeForUI = this.$utils.calculateAspectRatioFit({
            srcWidth: mediaItem.width,
            srcHeight: mediaItem.height,
            maxWidth: mediaItem.height < mediaItem.width ? 261 : 412,
            maxHeight: mediaItem.height > mediaItem.width ? 412 : 261,
            contentElWidth: $('#mediaItemsContainer').width(),
            ledPage: true
          })

          const cardWidth = this.$utils.is1920View()
            ? calculateSizeForUI.cardWidth
            : calculateSizeForUI.cardWidth + 2

          const ledEditProps = {
            isLed: true,
            width: calculateSizeForUI.width,
            height: calculateSizeForUI.height,
            previewWidth: calculateSizeForUI.previewWidth,
            previewHeight: calculateSizeForUI.previewHeight,
            nameWidth: calculateSizeForUI.nameWidth,
            aspectRatio: calculateSizeForUI.aspectRatio,
            cardStyle: {
              width: `${cardWidth}px`, // adjust width in 1280 for shadow
              height: 'auto'
            },
            previewElStyle: {
              width: `${calculateSizeForUI.previewWidth}px`,
              height: `${calculateSizeForUI.previewHeight}px`
            }
          }
          const { columns: columnsEstimate, baseWidth } = this.calculateNumberOfCardsPerRow(cardWidth, marginBase)
          // do not use estimated gutter size for fewer than row items
          const gutterEstimate = mediaItemsCount > columnsEstimate
            ? Math.floor((baseWidth - (columnsEstimate * cardWidth)) / (columnsEstimate - 1))
            : marginBase

          if (cardsCounter === 0 || (cardsCounter + 1) % columnsEstimate !== 0) {
            ledEditProps.cardStyle['margin-right'] = `${gutterEstimate}px`
          }

          mediaItem._edit = { ...mediaItem._edit, ...ledEditProps }
          if (this.isMediaComponentAsVideowall) {
            mediaItem.bezelsMask = {
              bezels: [...this.videowallBezels[this.getSelectedLedTag().value] ?? []],
              resolutionWidth: this.getSelectedLedTag().width,
              resolutionHeight: this.getSelectedLedTag().height,
              cardWidth: calculateSizeForUI.cardWidth,
              cardHeight: calculateSizeForUI.cardHeight
            }
          }
        } else {
          const gutterEstimate = Math.ceil(marginBase * (columns - 1) / columns)
          const fractionEstimate = Math.round((100 / columns) * 1000) / 1000
          // default number of cards per row is 3
          mediaItem._edit.cardStyle = {
            width: `calc(${fractionEstimate}% - ${gutterEstimate}px)`
          }

          // do not apply to the last card
          if (cardsCounter === 0 || (cardsCounter + 1) % columns !== 0) {
            mediaItem._edit.cardStyle['margin-right'] = `${marginBase}px`
          }
        }

        cardsCounter += 1
      }

      return mediaList
    },
    getSelectedLedTag () {
      let res = { width: 1080, height: 1080, name: '', dimension: '1080x1080', value: '', queryStringValue: '' }

      if (!_.isEmpty(this.mediaFilters.ledTags)) {
        let ledTag = this.mediaFilters.ledTags[0]

        if (!ledTag.startsWith('LED:')) {
          ledTag = `LED:${ledTag}`
        }

        res = this.$utils.parseLedTag(ledTag)
      }

      return res
    },
    getMediaList (params = {}) {
      const addRequestParams = params.addRequestParams ? params.addRequestParams : {}
      const resetMediaList = params.resetMediaList
      let requestParams = {
        dir: this.mediaFilters.orientation,
        max: 30,
        validity: 1,
        width: 424,
        use_qsa: 1
      }

      // Merge additional request params
      requestParams = { ...requestParams, ...addRequestParams }

      // For Alert page
      if (this.isMediaComponentAsAlert) {
        requestParams.alert = true
      }

      // Set SearchText filter
      const searchTextFilter = this.mediaFilters.searchText
      if (!_.isEmpty(searchTextFilter)) {
        requestParams.search = searchTextFilter
      }

      /**
       * Set Media types filter
       * Note: Add schedule type as scheduled=1 instead type=scheduled, (backend rule)
       */
      let mediaTypesFilter = _.clone(this.mediaFilters.mediaTypes)
      if (mediaTypesFilter.includes(CONST.MEDIA_TYPE_VALUE_SCHEDULED)) {
        requestParams.scheduled = true
        mediaTypesFilter = mediaTypesFilter.filter(t => t !== CONST.MEDIA_TYPE_VALUE_SCHEDULED)
      }
      if (!_.isEmpty(mediaTypesFilter)) {
        requestParams.type = mediaTypesFilter.join(',')
      }

      // Set Media tags filter
      const mediaTagsFilter = this.mediaFilters.mediaTags
      if (!_.isEmpty(mediaTagsFilter)) {
        requestParams.tags = JSON.stringify(mediaTagsFilter)
      }

      // Set PopId filter
      const popIdFilter = this.mediaFilters.popIds
      if (!_.isEmpty(popIdFilter) && !this.isMediaComponentAsAlert) {
        requestParams.popIds = JSON.stringify(popIdFilter)
      }

      // Set led tag for led page or video wall page
      if (this.isMediaComponentAsLed || this.isMediaComponentAsVideowall) {
        requestParams.led = this.getSelectedLedTag().dimension
        delete requestParams.dir
      }

      // Reset the media List
      if (resetMediaList) {
        this.handleResetMediaList()
      }

      const apiRequest = () => {
        const mediaList = this.$utils.clone(this.mediaList)
        this.error = ''
        this.isFetchingMedia = true

        if (!params.hideLoader) {
          this.$utils.mainLoader(true)
        }

        this.$api.get(`${CONFIG.API_BASE_URL}/media`, { params: requestParams })
          .then(response => {
            const newMediaBatch = response.data.media

            // Process newMediaBatch
            if (!_.isEmpty(newMediaBatch)) {
              mediaList.batchHistory.push(newMediaBatch) // Add new response data to batch history

              // Add each media item to itemsToShow
              for (const mediaItem of newMediaBatch) {
                let countDuplicate = 0
                for (const indx in mediaList.itemsToShow) {
                  if (mediaList.itemsToShow[indx].resourceId === mediaItem.resourceId) {
                    countDuplicate += 1
                  }
                }

                if (countDuplicate < 1) {
                  mediaList.itemsToShow.push(mediaItem)
                  this.listOfSvgToUpdateOnLoad.push(`mediaProjectFile_${mediaItem.resourceId}`)
                }
              }

              // Check the last media item if there are more item to fetch
              const lastMediaItem = newMediaBatch.length ? newMediaBatch[newMediaBatch.length - 1] : null
              if (lastMediaItem && lastMediaItem.moreItems) {
                mediaList.nextFetch.moreItems = true
                mediaList.nextFetch.exclusiveStartResourceId = lastMediaItem.resourceId
              } else {
                mediaList.nextFetch.moreItems = false
                mediaList.nextFetch.exclusiveStartResourceId = null
              }

              this.mediaList = this.filterMediaList(mediaList)
            }

            // Process mediaList whether it's empty for LED or VideoWall page.These pages has default "NEW" item and being handled by filterMediaList function.
            if (_.isEmpty(newMediaBatch) && (this.isMediaComponentAsLed || this.isMediaComponentAsVideowall)) {
              this.mediaList = this.filterMediaList(mediaList)
            }

            // Disable or Enable Upload
            let enableMediaUploadVal = false
            // User Storage contract layer
            if (response.data.upload) {
              if (response.data.upload) {
                enableMediaUploadVal = true
              }
            } else {
              enableMediaUploadVal = true
            }

            // User Rights contract layer
            if (!this.$currentUser.getRights().userCreate.uploadMedia) {
              enableMediaUploadVal = false
            }
            this.enableMediaUpload = enableMediaUploadVal
            this.fileUploaderStore.setUploaderConfig({
              disabledMediaUpload: !this.enableMediaUpload,
              targetComponent: this.isMediaComponentAsAlert ? 'alertPage' : 'filesPage'
            })

            // Run callback or close the loader
            if (params.callback) {
              params.callback(newMediaBatch)
            } else {
              this.$utils.mainLoader(false)
            }
          })
          .catch((error) => {
            this.error = error.message
            this.$utils.mainLoader(false)
            this.$apiError.handleUnauthorizedError(error, { originalRequest: () => { apiRequest() } })
          })
          .finally(() => {
            this.isFetchingMedia = false
          })
      }
      apiRequest()
    },
    /**
     * This will get media details in each item in media selection list
     * @param {Array} mediaSelection - list of selected media
     */
    getMediaSelectionDetails (mediaSelection) {
      const afterAPIRequest = (responseType, responseData, resourceId) => {
        const mediaSelection = this.$utils.clone(this.mediaSelection)
        let countRequest = 0

        mediaSelection.forEach((mediaItem, indx) => {
          if (mediaItem.resourceId === resourceId && responseType === 'success') {
            mediaSelection[indx] = responseData
            mediaSelection[indx]._getFullDetails = 'done'
          } else if (mediaItem.resourceId === resourceId && responseType === 'error') {
            mediaSelection[indx]._getFullDetails = 'done'
          }

          // If media has status(uploading), check and get unsent data in localStorage
          if (mediaItem.status) {
            const filesInLocalStorage = this.fileUploadStorage.getFiles().filter((file) => { return file !== null })

            const fileInLocalStorage = filesInLocalStorage.find((file) => { return (file._edit && file._edit.resourceId === mediaItem.resourceId) && (file._edit.targetComponent === 'filesPage' && file._edit.updateMediaDataOnceConversionFinished.updateMediaRequestData) })
            if (fileInLocalStorage) mediaSelection[indx] = { ...mediaItem, ...fileInLocalStorage._edit.updateMediaDataOnceConversionFinished.updateMediaRequestData, _getFullDetails: 'done' }
          }

          if (mediaSelection[indx]._getFullDetails === 'requesting') countRequest += 1
        })

        this.mediaSelection = mediaSelection

        if (countRequest === 0) {
          this.fetchingMediaSelectionDetails = false

          // Update media item data if there's an status property
          const updatedMediaSelectionData = this.mediaSelection
          const mediaList = this.$utils.clone(this.mediaList)

          for (const updateMediaData of updatedMediaSelectionData) {
            for (const indx in mediaList.itemsToShow) {
              const mediaItemToShow = mediaList.itemsToShow[indx]

              if (updateMediaData.resourceId === mediaItemToShow.resourceId) {
                if (mediaItemToShow.status) {
                  mediaList.itemsToShow[indx] = updateMediaData
                  delete mediaList.itemsToShow[indx]._getFullDetails
                }
                break
              }
            }
          }

          this.mediaList = this.filterMediaList(mediaList)
        }
      }

      for (const media of mediaSelection) {
        if (!media._getFullDetails) {
          media._getFullDetails = 'requesting'
          this.mediaSelection = mediaSelection
          this.fetchingMediaSelectionDetails = true

          const apiRequest = () => {
            const params = { use_qsa: 1 }
            this.$api.get(`${CONFIG.API_BASE_URL}/media/${media.resourceId}`, { params })
              .then(response => {
                afterAPIRequest('success', response.data, media.resourceId)
              })
              .catch((error) => {
                afterAPIRequest('error', error, media.resourceId)
                this.$apiError.handleUnauthorizedError(error, { originalRequest: () => { apiRequest() } })
              })
          }
          apiRequest()
        }
      }
    },
    getSchedulesByResolution (schedules = []) {
      if ((!this.isMediaComponentAsLed && !this.isMediaComponentAsVideowall) || _.isEmpty(this.getSelectedLedTag().value)) {
        return []
      }

      if (this.isMediaComponentAsVideowall || this.isMediaComponentAsLed) {
        return FindPlayerSchedulesByLEDTag(this.getSelectedLedTag().value, schedules)
      }
    },

    async getUserSchedules () {
      const { success, data: schedules } = await this.fetchAllSchedules(this.isMediaComponentAsAlert);

      if (!success) {
        EventBus.$emit(
          'SHOW_CUSTOM_NOTIFICATION',
          {
            type: 'error',
            alert: {
              message: this.$t('errors.unexpectedError')
            },
            confirmButtonText: this.$t('buttons.reload'),
            callbacks: {
              eventOKButtonClick: () => window.location.reload()
            }
          }
        );

        return;
      }

      this.userSchedules = schedules
      this.userSchedulesFilteredByResolution = this.getSchedulesByResolution(_.clone(schedules));
    },

    handleSelectAllVisibleMediaCard () {
      const currentMediaToShow = this.mediaList.itemsToShow
      let currentMediaItem = {}
      let eventKeys = { shiftKey: true, selectAll: true }
      let currentMediaItemPosition = 0

      if (currentMediaToShow.length === 1) {
        this.previousMediaItemSelected = { mediaItem: currentMediaToShow[0], mediaItemPosition: 0 }
        currentMediaItem = currentMediaToShow[0]
        eventKeys = {}
        currentMediaItemPosition = 0
      }

      if (currentMediaToShow.length > 1) {
        this.previousMediaItemSelected = { mediaItem: currentMediaToShow[0], mediaItemPosition: 0 }
        currentMediaItem = currentMediaToShow[currentMediaToShow.length - 1]
        eventKeys.shiftKey = true
        currentMediaItemPosition = currentMediaToShow.length - 1
      }

      this.handleMediaCardSelection(eventKeys, currentMediaItem, currentMediaItemPosition)
    },
    handleMediaCardOnClick (event, currentMediaItem, currentMediaItemPosition) {
      this.checkUnsavedData({
        nextAction: () => this.handleMediaCardSelection(event, currentMediaItem, currentMediaItemPosition)
      })
    },
    /**
     * Handle media card selection
     * @param {Object} event - mouse or keyboard event
     * @param {Object} currentMediaItem - selected media item details
     * @param {Number} currentMediaItemPosition - Position or index of the item in the array list
     */
    async handleMediaCardSelection (event, currentMediaItem, currentMediaItemPosition) {
      if (currentMediaItem.disabled) return

      const createLedProjectRouteParams = {
        path: '/template/default/led',
        query: {
          dimensions: this.getSelectedLedTag().dimension
        }
      }

      // Create Led Project
      if (currentMediaItem.newLED && !event.selectAll) {
        // Create led tag if ledTags is empty
        if (_.isEmpty(this.ledTags)) {
          const canvas = new Canvas(this.$api)
          const { error } = await canvas.createLED('', currentMediaItem.width, currentMediaItem.height)

          if (error) {
            EventBus.$emit('SHOW_CUSTOM_NOTIFICATION', {
              type: 'error',
              alert: {
                message: this.$t('playerSetup.CannotCreateLEDTag')
              }
            })
            return
          }
        }

        createLedProjectRouteParams.query.ledTags = this.getSelectedLedTag().queryStringValue
        this.$router.push(createLedProjectRouteParams)

        return
      }

      // Create Video wall Project
      if (currentMediaItem.newVideowall && !event.selectAll) {
        createLedProjectRouteParams.query.videowallTags = this.getSelectedLedTag().queryStringValue

        this.$router.push(createLedProjectRouteParams)

        return
      }

      // Reset audio player audio
      this.playAudio = { audio: null, isPlaying: false }
      this.updateAudioPlayer = null

      const isCTRLKeyPressed = this.isCtrlKeyPressed
      const isShiftKeyPressed = this.isShiftKeyPressed
      let mediaSelection = this.$utils.clone(this.mediaSelection)
      const mediaList = this.$utils.clone(this.mediaList)

      // Media Selection
      const mediaSelectionProcess = () => {
        // Clear direct trigger action to the media menu component
        this.mediaMenuTriggerPropertyAction = {}

        // Check Current Media Item selected if exist in Media Selection List
        const isMediaItemExistInMediaSelectionList = { exist: false, currentMediaItemPosition: 0 }

        for (const position in mediaSelection) {
          if (mediaSelection[position].resourceId === currentMediaItem.resourceId) {
            isMediaItemExistInMediaSelectionList.exist = true
            isMediaItemExistInMediaSelectionList.currentMediaItemPosition = position
            break
          }
        }

        // Keep the last media item clicked or selected
        const setLastMediaItemClicked = () => {
          this.previousMediaItemSelected = {
            mediaItem: currentMediaItem,
            mediaItemPosition: currentMediaItemPosition
          }
        }

        // Single Selection No Key pressed
        if (!isCTRLKeyPressed && !isShiftKeyPressed) {
          mediaSelection = []

          if (['shift', 'ctrl'].indexOf(this.previousKeyPressedInMediaSelection) >= 0) {
            mediaSelection.push(currentMediaItem)
          } else {
            if (!isMediaItemExistInMediaSelectionList.exist) {
              mediaSelection.push(currentMediaItem)
            }
          }

          setLastMediaItemClicked() // Keep as last media item clicked
          this.previousKeyPressedInMediaSelection = null // Keep as previous key pressed
        }

        // Multiple Selection with CTRL Key Pressed
        if (isCTRLKeyPressed || (isCTRLKeyPressed && !isShiftKeyPressed)) {
          if (!isMediaItemExistInMediaSelectionList.exist) {
            mediaSelection.push(currentMediaItem)
          } else {
            mediaSelection.splice(isMediaItemExistInMediaSelectionList.currentMediaItemPosition, 1)
          }

          setLastMediaItemClicked() // Keep as last media item clicked
          this.previousKeyPressedInMediaSelection = 'ctrl' // Keep as previous key pressed
        }

        // Multiple Selection with SHIFT Key Pressed
        if (isShiftKeyPressed) {
          const previousMediaItemSelected = this.$utils.clone(this.previousMediaItemSelected)
          const previousMediaItemPosition = previousMediaItemSelected.mediaItemPosition

          mediaSelection = []

          if (previousMediaItemPosition < currentMediaItemPosition) {
            for (let i = previousMediaItemPosition; i <= currentMediaItemPosition; i++) {
              mediaSelection.push(this.mediaList.itemsToShow[i])
            }
          }

          if (previousMediaItemPosition > currentMediaItemPosition) {
            for (let i = currentMediaItemPosition; i <= previousMediaItemPosition; i++) {
              mediaSelection.push(this.mediaList.itemsToShow[i])
            }
          }

          this.previousKeyPressedInMediaSelection = 'shift' // Keep as previous key pressed
        }

        // Exclude LED and Videowall NEW Card if selectAll event
        if (event.selectAll) {
          mediaSelection = mediaSelection.filter(item => !item.newVideowall && !item.newLED)
        }

        this.mediaSelection = mediaSelection
        this.mediaList = this.filterMediaList(mediaList)

        if (mediaSelection.length > 0) {
          this.getMediaSelectionDetails(mediaSelection)
        }
      }

      this.hideMediaMenuComponentTemplate = false // Make sure media menu component template is visible
      mediaSelectionProcess()
    },
    /**
     * This will clear media selection if white background is clicked
     * @param {Object} event
     */
    handleWhiteBackgroundOnClick (event) {
      const mediaList = this.$utils.clone(this.mediaList)
      const isClearMediaSelection = $(event.srcElement).hasClass('clear-media-selection-if-clicked')

      if (isClearMediaSelection === true) {
        this.mediaSelection = []
        this.mediaList = this.filterMediaList(mediaList)
      }
    },
    handleResetMediaList (params) {
      let mediaList = this.$utils.clone(this.mediaList)
      const clearProp = {
        itemsToShow: [],
        nextFetch: {
          moreItems: false,
          exclusiveStartResourceId: null
        },
        batchHistory: []
      }

      mediaList = { ...mediaList, ...clearProp }
      params = {}

      if (typeof params === 'object' && params.resetFilter) {
        mediaList = { ...mediaList, reqFilter: { type: [], search: '' } }
      }

      this.mediaList = mediaList
    },
    /**
     * Hook function when the media menu sub-component change
     * @param {Object} event
     */
    handleMediaMenuChange (event) {
      if (event.action === 'UPDATE_MEDIA_DATA') {
        this.confirmForceToDeleteMedia = { showDialog: false, items: [], confirmed: false } // Reset force delete media pop-up

        /** If request type is put then update the media list data */
        if (event.payload.data.requestType === 'put') {
          const getUpdatedMediaData = (orgMediaResourceId) => {
            // Get Updated Data
            let updatedData = null
            for (const indx in event.payload.data.updatedMedia) {
              const updatedMedia = event.payload.data.updatedMedia[indx]

              if ((orgMediaResourceId === updatedMedia.resourceId) && updatedMedia.response.type === 'success') {
                updatedData = updatedMedia.response.responseData
              }
            }

            return updatedData
          }

          // Let's Update Media Selection List Data
          const mediaSelection = this.$utils.clone(this.mediaSelection)
          mediaSelection.forEach((orgMedia, indx) => {
            const updatedMedia = getUpdatedMediaData(orgMedia.resourceId)
            mediaSelection[indx] = updatedMedia !== null ? (updatedMedia) : (orgMedia)
          })
          this.mediaSelection = mediaSelection

          // Let's update current media list
          const mediaList = this.$utils.clone(this.mediaList)

          // Update media items in Media List
          mediaList.itemsToShow.forEach((orgMediaItem, mediaItemIndx) => {
            const updatedMedia = getUpdatedMediaData(orgMediaItem.resourceId)

            mediaList.itemsToShow[mediaItemIndx] = updatedMedia !== null ? (updatedMedia) : (orgMediaItem)
          })

          // Update Media List by batch in history
          mediaList.batchHistory.forEach((batch, batchIndx) => {
            for (const indx in batch) {
              const orgMedia = batch[indx]
              const updatedMedia = getUpdatedMediaData(orgMedia.resourceId)

              mediaList.batchHistory[batchIndx][indx] = updatedMedia !== null ? (updatedMedia) : (orgMedia)
            }
          })
          this.mediaList = this.filterMediaList(mediaList)

          // Process those media items with error response
          const errorRequests = event.payload.data.updatedMedia.filter((mediaData) => mediaData.response.type === 'error')

          if (errorRequests.length > 0) {
            this.error = `${errorRequests.length} media items with error response`
          } else {
            this.error = ''
          }

          // Get tags summary to keep tag for suggestions updated
          this.getTagsSummary()
          this.hideMediaMenuComponentTemplate = false
        }

        // If request type is delete then fetch schedule list
        if (event.payload.data.requestType === 'delete') {
          this.mediaSelection = []

          // We should check if there are media that are in used. If so, then show the delete confirmation popup
          const mediaSelectionBeforRequest = this.$utils.clone(event.payload.data.mediaSelectionBeforRequest)
          const confirmToForceDeleteMediaItems = []

          event.payload.data.updatedMedia.forEach((mediaItem, indx) => {
            const hasResponse = mediaItem.response.responseData.response
            if (hasResponse && (mediaItem.response.responseData.response.status === 412 || mediaItem.response.responseData.response.status === 409)) {
              const newMediaItem = this.$utils.clone(mediaItem)

              // We will get the full details of this media from our original media selection before the request
              for (const i in mediaSelectionBeforRequest) {
                const origMediaDetailsSrc = mediaSelectionBeforRequest[i]

                if (mediaItem.origDetails.resourceId === origMediaDetailsSrc.resourceId) {
                  confirmToForceDeleteMediaItems.push(newMediaItem)
                  break
                }
              }
            }

            // Remove from the list
            if (mediaItem.response.type === 'success') {
              const mediaList = this.$utils.clone(this.mediaList)

              // Delete from Batch History
              mediaList.batchHistory.forEach((mediaByBatch) => {
                const updatedMediaByBatch = this.$utils.deleteItemFromList(mediaByBatch, mediaItem, 'resourceId')
                mediaByBatch = updatedMediaByBatch
              })

              // Delete From Items to show
              const newMediaListToShow = this.$utils.deleteItemFromList(mediaList.itemsToShow, mediaItem, 'resourceId')
              mediaList.itemsToShow = newMediaListToShow
              this.handleResetMediaList()
              this.mediaList = this.filterMediaList(mediaList)
              this.mediaMenuTriggerPropertyAction = {}
            }
          })

          // Show confirmation dialog if force to delete media from project
          if (confirmToForceDeleteMediaItems.length > 0) {
            const confirmForceToDeleteMedia = this.$utils.clone(this.confirmForceToDeleteMedia)
            confirmForceToDeleteMedia.showDialog = true
            confirmForceToDeleteMedia.items = confirmToForceDeleteMediaItems

            this.confirmForceToDeleteMedia = confirmForceToDeleteMedia
            this.hideMediaMenuComponentTemplate = true

            // Send global event to show confirmation dialog
            const deleteCount = confirmForceToDeleteMedia.items.length
            const isMultipleDelete = deleteCount > 1
            EventBus.$emit('SHOW_CUSTOM_NOTIFICATION', {
              type: 'warning',
              title: this.$t('confirm.deleteMedia'),
              descriptionI18n: isMultipleDelete ? { key: 'media.multipleMediaDeleteConfirmation', params: { mediaCount: deleteCount } } : { key: 'media.mediaDeleteConfirmation', params: { mediaName: confirmForceToDeleteMedia.items[0].origDetails.name } },
              alert: {
                message: isMultipleDelete ? this.$t('media.multipleMediaDeleteWarning') : this.$t('media.mediaDeleteWarning')
              },
              preview: {
                items: confirmForceToDeleteMedia.items,
                mediaOrientation: this.mediaOrientation
              },
              confirmButtonText: this.$t('buttons.deleteMedia'),
              confirmButtonIcon: 'flowbite:trash-bin-solid',
              callbacks: {
                eventOnClose: () => {
                  const confirmForceToDeleteMedia = this.$utils.clone(this.confirmForceToDeleteMedia)
                  confirmForceToDeleteMedia.showDialog = false
                  this.confirmForceToDeleteMedia = confirmForceToDeleteMedia
                },
                eventOKButtonClick: () => {
                  this.handleConfirmForceDeleteMedia()
                }
              }
            })
          }

          // Update media listing and remove tagSlider selection in localStorage
          const prevSelectedTags = this.$dataStore.getStore().selectedTagsFromSlider ? this.$dataStore.getStore().selectedTagsFromSlider : {}
          const selectedTags = prevSelectedTags.mediaFiles && prevSelectedTags.mediaFiles.length ? prevSelectedTags.mediaFiles : []

          // Clear media selection from tags slider
          if (selectedTags.length) {
            const selectedTagsFromSlider = this.$dataStore.getStore().selectedTagsFromSlider ? this.$dataStore.getStore().selectedTagsFromSlider : {}
            selectedTagsFromSlider.mediaFiles = []
            this.$dataStore.setStore({ selectedTagsFromSlider })
            this.tagsSelectionRequest = { ref: this.$utils.generateUniqueID(), tags: [] }

            this.$utils.mainLoader(true)
            this.getMediaList({ resetMediaList: true })
          }
        }

        // Force to reset the media selection and make sure media menu componenent template is reset to be visible
        if (['put', 'delete'].indexOf(event.payload.data.requestType) >= 0) {
          if (!event.payload.data.autoSave) {
            this.mediaSelection = []
          }

          this.mediaList = this.filterMediaList(this.mediaList)
          this.refreshTagSlider = { ref: this.$utils.generateUniqueID(), refresh: true, mediaChanged: true }
          this.refreshPopIdsSlider = { ref: this.$utils.generateUniqueID(), refresh: true }
          this.$utils.updatePageComponentStyle({ name: this.isMediaComponentAsLed ? 'ledPage' : 'mediaFiles' })
        }
      }

      if (event.action === 'RESET_MEDIA_MENU_TEMPLATE_VISIBILITY') {
        this.hideMediaMenuComponentTemplate = false
        this.mediaSelection = []
        this.mediaList = this.filterMediaList(this.mediaList)
      }
    },
    handleImagePreviewIconClick (imagePreview) {
      this.imagePreview = {
        dialog: true,
        image: imagePreview
      }
    },
    /**
     * Handle Play video preview in the pop-up
     * @param {Array} videopreviews - list of video previous
     */
    handleVideoPlayIconClick (videopreviews) {
      const playVideo = this.$utils.clone(this.playVideo)

      playVideo.dialog = true
      playVideo.videopreviews = videopreviews

      this.playVideo = playVideo
      this.playAudio = { audio: null, isPlaying: false }
    },
    handleAudioPlayIconClick (audio) {
      if (!this.playAudio.audio || (this.playAudio.audio && this.playAudio.audio.resourceId !== audio.resourceId)) {
        const playAudio = this.$utils.clone(this.playAudio)
        playAudio.audio = audio
        playAudio.isPlaying = true
        this.playAudio = playAudio
      } else {
        const updateAudioPlayer = { ref: this.$utils.generateUniqueID(), action: 'playAudioPreview', data: this.playAudio.audio }
        this.updateAudioPlayer = updateAudioPlayer

        const playAudio = this.$utils.clone(this.playAudio)
        playAudio.isPlaying = true
        this.playAudio = playAudio
      }
    },
    handleAudioPauseIconClick () {
      const updateAudioPlayer = { ref: this.$utils.generateUniqueID(), action: 'pauseAudioPreview' }
      this.updateAudioPlayer = updateAudioPlayer

      const playAudio = this.$utils.clone(this.playAudio)
      playAudio.isPlaying = false
      this.playAudio = playAudio
    },
    handleMediaOrientationClick (orientation) {
      this.mediaOrientation = orientation
      this.$filtersRepository.saveOrientation(orientation)

      const getMediaListParams = {
        prevAction: 'orientationChanged',
        addRequestParams: { width: 396 },
        resetMediaList: true
      }

      // Set for vertival width
      if (orientation === CONST.ORIENTATION_VERTICAL) {
        getMediaListParams.addRequestParams.width = 264
      }

      this.getMediaList(getMediaListParams)
    },
    /**
     * Cleanup media upload file list. Remove items with error and except with status code 401
     */
    cleanupNewMediaUploadFileList  () {
      const newMediaUploadFileList = this.newMediaUploadFileList.filter((item) => {
        return item._edit.requests.createMediaStatus !== 'error' || item._edit.requests.createMediaStatusCode === 401
      })

      this.newMediaUploadFileList = newMediaUploadFileList
    },
    /**
     * Send an event to trigger fileUploader click event
     */
    handleFileUploadIconClick () {
      this.fileUploaderStore.setUploaderConfig({
        triggerClickDropzone: true,
        targetComponent: this.isMediaComponentAsAlert ? 'alertPage' : 'filesPage'
      })
    },
    /**
     * Hook function when fileUploader component change
     */
    handleNewUploadedFiles (newVal) {
      // Update Media listing
      if (newVal.prevAction === 'processNewFilesWithInstance') {
        this.getMediaList({ resetMediaList: true })
      } else if (newVal.prevAction === 'finishedFile') {
        const updatedMediaItem = newVal.updatedFile?._edit?.uploadRequests?.createMediaInstanceResponseData
        if (!updatedMediaItem) return

        // Update Current Media List
        const mediaList = this.$utils.clone(this.mediaList)
        mediaList.itemsToShow = this.$utils.updateItemFromList(mediaList.itemsToShow, updatedMediaItem, 'resourceId') // Update media items in Media List
        mediaList.batchHistory.forEach((mediaListByBatch) => { // Update Media List by batch
          mediaListByBatch = this.$utils.updateItemFromList(mediaListByBatch, updatedMediaItem, 'resourceId')
        })
        this.mediaList = this.filterMediaList(mediaList)
      }
    },
    /**
     * The purpose of this function is to relay mouse hover and mouse out events to the thumbnail iframe
     * so that content slides animation can be triggered in the thumbnail iframe.
     * @param {*} event original mouseOver or mouseOut event coming from the media card
     * @param {*} mediaItem target media item to relay the event to the thumbnail iframe
     * @returns
     */
    async relayMouseHoverThumbnailEvent (event, mediaItem) {
      const iframeId = `mediaProjectFile_${mediaItem.resourceId}`
      const iframe = $(`#${iframeId}`);

      if (iframe.length === 0) {
        return;
      }

      await new Promise(resolve => setTimeout(resolve, 300));
      await this.$nextTick(() => iframe[0].contentWindow.postMessage({ mouse: event }, '*'));
    },
    handleMediaCardMouseHoverAndMouseOut (mouseEvent, hoveredItem) {
      const mediaList = this.$utils.clone(this.mediaList)
      const isMouseout = mouseEvent === 'mouseout'
      const isMouseover = mouseEvent === 'mouseover'

      for (const indx in mediaList.itemsToShow) {
        const item = mediaList.itemsToShow[indx]
        if (item.resourceId === hoveredItem.resourceId) {
          item._edit.isHover = isMouseover
          item._edit.isActive = isMouseover
          this.hoveredItem = item
          break
        }
      }

      this.mediaList = mediaList

      // should be done after changing the DOM
      if (isMouseover || isMouseout) {
        this.relayMouseHoverThumbnailEvent(mouseEvent, hoveredItem);
      }

      // Handle copy and paste using Mouse event
      const enableCopyPasteEvent = this.enableCopyMedia && ['project'].indexOf(this.hoveredItem.type) >= 0
      let mediaOnHoverCopyPasteContainerEl = document.getElementById(MEDIA_COPY_PASTE_TEXTAREA_ID)

      if (enableCopyPasteEvent && !mediaOnHoverCopyPasteContainerEl) {
        mediaOnHoverCopyPasteContainerEl = document.createElement('textarea')
        mediaOnHoverCopyPasteContainerEl.id = MEDIA_COPY_PASTE_TEXTAREA_ID
        Object.assign(mediaOnHoverCopyPasteContainerEl.style, { opacity: 0, position: 'fixed', height: '0px', width: '0px', left: 0, top: 0, zIndex: 1 })
        document.body.appendChild(mediaOnHoverCopyPasteContainerEl)
        mediaOnHoverCopyPasteContainerEl.focus()

        // Handle copy event
        mediaOnHoverCopyPasteContainerEl.addEventListener('copy', (event) => {
          if (['project'].indexOf(this.hoveredItem.type) >= 0 && !this.inlineEditName.isInputVisible) {
            const itemToCopy = JSON.stringify({
              source: this.getSelectedLedTag(),
              item: this.hoveredItem,
            }, undefined, 2);
            mediaOnHoverCopyPasteContainerEl.value = JSON.stringify(this.hoveredItem, undefined, 2)
            event.clipboardData.setData('text/plain', itemToCopy)
          } else {
            mediaOnHoverCopyPasteContainerEl.value = ''
          }

          event.preventDefault()
        })

        // Handle paste Event
        mediaOnHoverCopyPasteContainerEl.addEventListener('paste', (event) => {
          const text = (event.clipboardData || window.clipboardData).getData('text')

          if (!text || (text && !text.length)) return

          const copiedData = JSON.parse(text);
          const mediaData = copiedData.item;
          const selectedTag = this.getSelectedLedTag().value

          if (copiedData.source.value !== selectedTag) {
            console.warn('Skip copying of media for different resolutions')
            return;
          }

          this.handleCopyMediaIconClick(mediaData)

          event.preventDefault()
        })
      } else if (enableCopyPasteEvent && (mediaOnHoverCopyPasteContainerEl && !this.inlineEditName.isInputVisible)) {
        mediaOnHoverCopyPasteContainerEl.focus()
        mediaOnHoverCopyPasteContainerEl.select()
      }
    },
    handleMediaCardRemoveIconMouseHoverAndMouseOut (mouseEvent, mediaItem) {
      if (mediaItem.disabled) {
        return
      }

      if (mouseEvent === 'mouseover') {
        this.allMediaItemActionIconHover = true
        return
      }

      this.allMediaItemActionIconHover = false
    },
    handleConfirmForceDeleteMedia () {
      const confirmForceToDeleteMedia = this.$utils.clone(this.confirmForceToDeleteMedia)
      confirmForceToDeleteMedia.confirmed = true
      this.confirmForceToDeleteMedia = confirmForceToDeleteMedia
    },
    handleMediaCardRemoveIconClick (mediaItem) {
      const mediaMenuTriggerPropertyAction = {
        action: 'deleteMediaItem',
        mediaSelection: this.mediaSelection,
        refNum: Date.now() + Math.random()
      }

      if (mediaItem.disabled) {
        // Reset media selection and media list
        this.mediaSelection = []
        this.mediaList = this.filterMediaList(this.mediaList)

        // Set new media selection and hide media menu template, just to use the logic of the component
        this.mediaSelection = [mediaItem]
        this.hideMediaMenuComponentTemplate = true
      }

      this.mediaMenuTriggerPropertyAction = mediaMenuTriggerPropertyAction
    },
    handleEditProjectClick (mediaItem) {
      let redirectPath = mediaItem.type === 'audio-playlist' ? `/project-audio/${mediaItem.resourceId}/edit-frame` : `/project-edit/${mediaItem.resourceId}/edit-frame`
      const encodedQueryString = this.$utils.encodeQueryString(this.$route.query)

      if (!_.isEmpty(encodedQueryString) && redirectPath.includes('/project-edit')) {
        redirectPath = `${redirectPath}?${encodedQueryString}`
      }

      this.$router.push(redirectPath)
    },
    handleCopyMediaIconClick (mediaItem) {
      const getProjectDetails = () => {
        const params = { use_qsa: 1 }
        this.$utils.mainLoader(true)
        this.$api.get(`${CONFIG.API_BASE_URL}/project/${mediaItem.resourceId}`, { params })
          .then(response => {
            let projectData = response.data

            // Send post request for new project
            const createNewProject = () => {
              projectData = this.$utils.filterProjectDataForBackend({ project: projectData })
              this.$api.post(`${CONFIG.API_BASE_URL}/project`, projectData)
                .then(response => {
                  const newMediaResourceId = response.data.resourceId

                  // Get Media Details
                  const getNewMediaDetails = () => {
                    const params = { use_qsa: 1, width: 1280 }
                    this.$api.get(`${CONFIG.API_BASE_URL}/media/${newMediaResourceId}`, { params })
                      .then(response => {
                        const newCreatedMedia = response.data
                        // Update Media List
                        const mediaList = this.$utils.clone(this.mediaList)
                        mediaList.batchHistory[0].unshift(newCreatedMedia)
                        mediaList.itemsToShow.unshift(newCreatedMedia)
                        this.mediaList = this.filterMediaList(mediaList)
                        this.listOfSvgToUpdateOnLoad.push(`mediaProjectFile_${newMediaResourceId}`)
                        this.$utils.mainLoader(false)
                      })
                      .catch(error => {
                        this.$apiError.handleAPIRequestError(error, { originalRequest: () => { getNewMediaDetails() }, showDialogNotification: true })
                      })
                  }
                  getNewMediaDetails()
                })
                .catch(error => {
                  this.$utils.mainLoader(false)
                  this.$apiError.handleAPIRequestError(error, { originalRequest: () => { createNewProject() }, showDialogNotification: true })
                })
            }
            createNewProject()
          })
          .catch(error => {
            this.$utils.mainLoader(true)
            this.$apiError.handleAPIRequestError(error, { originalRequest: () => { getProjectDetails() }, showDialogNotification: true })
          })
      }
      getProjectDetails()
    },
    handleTagSliderChange ($event) {
      this.setMediaFiltersFromRepository()
      this.refilterLedTags = { ref: this.$utils.generateUniqueID(), prevFilterAction: $event.action }

      // New Tags Slider Selection
      if ($event.action === CONST.EMIT_EVENT_NEW_SELECTED_TAGS_FROM_SLIDER) {
        this.selectedTagsFromSlider = $event.payload.data
        this.getMediaList({ resetMediaList: true })
      }

      if ($event.action === CONST.EMIT_EVENT_REFETCH_PARENT_COMPONENT_LIST) {
        this.getMediaList({ prevAction: 'refetchTagsChanged', resetMediaList: true })
      }

      // List of Tags from Slider
      if ($event.action === CONST.EMIT_EVENT_DONE_FETCHING_TAGS_FROM_SLIDER) {
        this.tagsSummaryFromTagsSlider = $event.payload.data
      }

      if ($event.action === CONST.EMIT_EVENT_ALL_TAG_SELECTED_FROM_SLIDER) {
        this.selectedTagsFromSlider = []
        this.setDefaultData({ prevAction: 'ALL_tagSelectedFromSlider' })
      }

      // Reset media selection
      if ($event.action === CONST.EMIT_EVENT_UPDATING_TAG_FROM_SLIDER) {
        const oldTagValue = $event.payload.data.oldTagValue
        const newTagValue = $event.payload.data.newTagValue
        this.mediaSelection = []
        this.mediaList = this.filterMediaList(this.mediaList)
        this.$utils.updatePageComponentStyle({ name: this.isMediaComponentAsLed ? 'ledPage' : 'mediaFiles' })

        // Update selected tags from slider data
        this.selectedTagsFromSlider = this.selectedTagsFromSlider.map(t => {
          if (t === oldTagValue) {
            t = newTagValue
          }
          return t
        })

        // If LED page, update inline tags inside led tags
        if (this.isMediaComponentAsLed || this.isMediaComponentAsVideowall) {
          this.ledTags = this.ledTags.map(ledTag => {
            for (const i in ledTag.inline) {
              if (ledTag.inline[i] === oldTagValue) {
                ledTag.inline[i] = newTagValue
              }
            }

            return ledTag
          })
        }

        this.handleClientSearch()
      }

      // Reset media selection
      if ($event.action === CONST.EMIT_EVENT_DELETED_TAG_FROM_SLIDER) {
        this.mediaSelection = []
        this.setDefaultData({ prevAction: 'deletedTagItem' })
        this.$utils.updatePageComponentStyle({ name: this.isMediaComponentAsLed ? 'ledPage' : 'mediaFiles' })

        // Update slider tag selection
        const selectedTagsFromSlider = this.$utils.clone(this.selectedTagsFromSlider)
        const deletedTag = $event.payload.data.prevVal ? $event.payload.data.prevVal : null
        const getIndx = selectedTagsFromSlider.indexOf(deletedTag)

        if (getIndx >= 0) selectedTagsFromSlider.splice(selectedTagsFromSlider, 1)

        this.selectedTagsFromSlider = selectedTagsFromSlider
        this.handleClientSearch()
      }
    },
    handlePopIdsSliderChange ($event) {
      this.setMediaFiltersFromRepository()
      this.refilterLedTags = {
        ref: this.$utils.generateUniqueID(),
        prevFilterAction: $event.action
      }

      // New PopIds selected from slider
      if ($event.action === CONST.EMIT_EVENT_NEW_SELECTED_POP_ID_FROM_SLIDER) {
        this.selectedPopIdsFromSlider = $event.payload.data
        this.getMediaList({ resetMediaList: true })
      }

      if ([CONST.EMIT_EVENT_ALL_SELECTED_POPID_FROM_SLIDER, CONST.EMIT_EVENT_DELETED_POPID_FROM_SLIDER].includes($event.action)) {
        this.selectedPopIdsFromSlider = []
        this.getMediaList({ resetMediaList: true })
      }

      // List of PopIds from Slider
      if ($event.action === CONST.EMIT_EVENT_LIST_OF_POP_IDS_FROM_SLIDER) {
        this.popIdsSummaryFromTagsSlider = $event.payload.data
      }
    },
    handleLedTagSliderChange ($event) {
      this.setMediaFiltersFromRepository()

      if ($event.action === CONST.EMIT_EVENT_NEW_SELECTED_LED_TAG) {
        this.getMediaList({
          resetMediaList: true,
          callback: () => { this.getUserSchedules() }
        })
      }

      if ($event.action === CONST.EMIT_EVENT_DELETED_LED_TAG) {
        this.getTagsSummary(() => {
          this.getMediaList({ resetMediaList: true })
        })
      }
    },
    handleSVGOnLoad (params = {}) {
      const listOfSvgToUpdateOnLoad = this.listOfSvgToUpdateOnLoad
      if (listOfSvgToUpdateOnLoad.indexOf(params.elId) >= 0) {
        this.postMessageToSVG({
          type: 'iframeOnLoad',
          projectId: params.elId,
          message: { projectOptions: { dir: this.mediaFilters.orientation.toLowerCase() } }
        })

        listOfSvgToUpdateOnLoad.splice(listOfSvgToUpdateOnLoad.indexOf(params.elId), 1)
      }

      this.listOfSvgToUpdateOnLoad = listOfSvgToUpdateOnLoad
    },
    handleSVGMessage (event) {
      // Make sure event.origin is from the app to prevent accepting message out of nowhere and event.data is an object
      const sessionStage = useCognitoAuthStore().sessionStage ?? CONFIG.GLOBAL_STAGE;
      const apiOrigin = (new URL(this.$utils.getStageAPIUrl(sessionStage))).origin
      if ((event.origin !== apiOrigin) || (!_.isPlainObject(event.data))) return

      if (event.data && event.data.loaded && event.data.uri) {
        $(`.media-project-file[src="${event.data.uri}"]`).addClass('iframe-loaded')
      }

      if (event.data && event.data.status && event.data.status === 'finished') {
        if (event.data.media) {
          const updatedMediaItem = event.data.media

          // Update Current Media List
          const mediaList = this.$utils.clone(this.mediaList)
          mediaList.itemsToShow = this.$utils.updateItemFromList(mediaList.itemsToShow, updatedMediaItem, 'resourceId') // Update media items in Media List
          mediaList.batchHistory.forEach((mediaListByBatch) => { // Update Media List by batch
            mediaListByBatch = this.$utils.updateItemFromList(mediaListByBatch, updatedMediaItem, 'resourceId')
          })
          this.mediaList = this.filterMediaList(mediaList)
        }
      }
    },
    postMessageToSVG (params) {
      const selectedSchedulePreviewEl = $(`#${params.projectId}`)
      if (selectedSchedulePreviewEl.length) {
        const msg = params.message ? params.message : { projectOptions: { dir: this.mediaOrientation.toLowerCase() } }
        selectedSchedulePreviewEl[0].contentWindow.postMessage(msg, '*')
      }
    },
    addMessageListener () {
      if (!this.handleSVGMessageEventListenerFunction) {
        this.handleSVGMessageEventListenerFunction = this.handleSVGMessage.bind(this)
        window.addEventListener('message', this.handleSVGMessageEventListenerFunction)
      }
    },
    removeMessageListener () {
      if (this.handleSVGMessageEventListenerFunction) {
        window.removeEventListener('message', this.handleSVGMessageEventListenerFunction)
        this.handleSVGMessageEventListenerFunction = null
      }
    },
    handleAudioPlayerChange ($event) {
      if ($event.action === 'ENDED_SONG') {
        this.playAudio = { audio: null, isPlaying: false }
      }
    },
    async getTagsSummary (callback = () => {}) {
      const containsSelectedValidTag = (tags, repositoryValue) => {
        return (
          repositoryValue.length > 0 &&
          _.intersection(
            tags.map(tag => this.$utils.parseLedTag(tag.value)).map(tag => tag.queryStringValue),
            repositoryValue
          ).length > 0
        )
      }

      await this.fetchTags()
      /**
       * Create tags summary.This is commonly used for suggestions when adding new tag.
       * Resource tags is always included
       */
      let tagsSummary = this.tagsStoreResourceTags ?? [] // response.data.resourceTags ?? []

      // Add Player tags to summary
      if (_.isArray(this.tagsStorePlayerTags)) {
        tagsSummary = _.concat(tagsSummary, this.tagsStorePlayerTags)
      }

      // Filter led tags for LED page
      let ledTags = this.tagsStoreLedTags ?? []
      let videowallTags = this.tagsStoreVideowallTags ?? []

      if (this.isMediaComponentAsLed) {
        if (!hasAccessToLEDPage(this.$currentUser, ledTags)) {
          this.$utils.mainLoader(false)
          this.$router.push('/menu')
          return
        }

        // If current user has CREATE rights + user tags OR CREATE_LOCK (modify) rights + user tags, filter led tags inline with assigned user tags
        if ((this.$currentUser.hasGroupUserCreate() || this.$currentUser.hasGroupUserCreateLock()) && !_.isEmpty(this.$currentUser.getUserTags())) {
          ledTags = ledTags.filter(ledTag => !_.isEmpty(_.intersection(ledTag.inline, this.$currentUser.getUserTags())))
        }

        // Tags summary inline with led
        tagsSummary = tagsSummary.filter(resourceTag => !_.isEmpty(ledTags.find(ledTag => ledTag.inline.includes(resourceTag.name))))
        this.ledTags = this.$utils.filterTagOptionsForUI({ tags: ledTags, showLabel: true, removeLEDPrefixForLabel: true })

        // Select first led tag by default if no led selected, or invalid tag selected
        if (!_.isEmpty(this.ledTags) && !containsSelectedValidTag(this.ledTags, this.mediaFilters.ledTags)) {
          this.mediaFilters.ledTags = [this.ledTags[0].queryStringValue]
          this.$filtersRepository.saveLedTags([this.ledTags[0].queryStringValue])
        }

        this.isLedTagsReady = true
      } else if (this.isMediaComponentAsVideowall) {
        if (!hasAccessToVideowallPage(this.$currentUser, videowallTags)) {
          this.$utils.mainLoader(false)
          this.$router.push('/menu')
          return
        }

        // If current user has CREATE rights + user tags OR CREATE_LOCK (modify) rights + user tags, filter led tags inline with assigned user tags
        if ((this.$currentUser.hasGroupUserCreate() || this.$currentUser.hasGroupUserCreateLock()) && !_.isEmpty(this.$currentUser.getUserTags())) {
          videowallTags = videowallTags.filter(ledTag => !_.isEmpty(_.intersection(ledTag.inline, this.$currentUser.getUserTags())))
        }

        // Tags summary inline with led
        tagsSummary = tagsSummary.filter(resourceTag => !_.isEmpty(ledTags.find(ledTag => ledTag.inline.includes(resourceTag.name))))
        this.ledTags = this.$utils.filterTagOptionsForUI({ tags: videowallTags, showLabel: true, videowall: true })

        // Select first led tag by default if no led selected
        if (!_.isEmpty(this.ledTags) && !containsSelectedValidTag(this.ledTags, this.mediaFilters.ledTags)) {
          this.mediaFilters.ledTags = [this.ledTags[0].queryStringValue]
          this.$filtersRepository.saveVideowallTags([this.ledTags[0].queryStringValue])
        }

        if (!_.isEmpty(this.ledTags)) {
          this.videowallBezels = _.fromPairs(this.ledTags.map(item => [item.value, item.bezels]))
        }

        this.isLedTagsReady = true
      } else {
        // Get all tags NOT inline with LED
        tagsSummary = tagsSummary.filter(resourceTag => _.isEmpty(ledTags.find(ledTag => ledTag.inline.includes(resourceTag.name))))
        this.setMediaFiltersFromRepository()
      }

      this.tagsSummaryForSuggestions = this.$utils.filterTagOptionsForUI({ tags: _.uniqBy(tagsSummary, 'name') }) // remove duplicates by name

      if (_.isFunction(callback)) {
        callback()
      }

      if (this.tagsStoreFetchErrors) {
        this.$apiError.handleUnauthorizedError(this.tagsStoreFetchErrors, { originalRequest: () => { this.getTagsSummary() } })
      }
    },
    ctrlAndShiftKeyPressedWatcher () {
      // Track ctrl/cmd and shift key state globally
      $(document).on('keydown', (e) => {
        const key = e.key
        if (e.ctrlKey || e.metaKey || key === 'Control' || key === 'Meta') {
          this.isCtrlKeyPressed = true
        }
        if (e.shiftKey || key === 'Shift') {
          this.isShiftKeyPressed = true
        }
      })

      $(document).on('keyup', (e) => {
        const key = e.key
        // Only unset if actual ctrl/cmd released
        if (!e.ctrlKey && !e.metaKey && (key === 'Control' || key === 'Meta')) {
          this.isCtrlKeyPressed = false
        }
        // Only unset if actual shift released
        if (!e.shiftKey && key === 'Shift') {
          this.isShiftKeyPressed = false
        }
      })
    },
    windowClickedWatcher () {
      $(window).on('click', (e) => {
        const targetEl = e.target ? $(e.target) : null
        const isWhiteBackgroundClicked = targetEl && (targetEl.hasClass('workspace') || targetEl.hasClass('search-bar-content'))

        // Focus copy/paste textarea data wrapper
        const mediaCopyPasteTextareaEl = $(`#${MEDIA_COPY_PASTE_TEXTAREA_ID}`)
        const copiedTemplate = mediaCopyPasteTextareaEl.length && mediaCopyPasteTextareaEl.val().length > 0

        if (isWhiteBackgroundClicked && copiedTemplate) {
          mediaCopyPasteTextareaEl.focus()
        }
      })
    },
    onEnter (event) {
      if (event.target.id === 'media-item-name-input') {
        event.target.blur()
      }
    },
    /**
     * Hook function for draggable as dropzone for `tags-shared` group
     *
     * @param {Object} event - VueDraggable add event
     * @param {Object} mediaItem - Media item
     */
    handleTagDraggableAdd (event, mediaItem) {
      const draggedEl = event.item // element being dragged
      const draggedDataset = draggedEl?.dataset // data-* attributes
      const dataDraggableType = draggedDataset?.draggableType ?? draggedEl.getAttribute('data-draggable-type') // 'tag' or 'popId'
      const dataDraggableValue = draggedDataset?.draggableValue ?? draggedEl.getAttribute('data-draggable-value') // tag or popId value

      if (!draggedEl || !dataDraggableType || !dataDraggableValue) {
        return
      }

      const tagVal = dataDraggableType === 'tag' ? dataDraggableValue : null
      const popIdVal = dataDraggableType === 'popId' ? dataDraggableValue : null
      const targetMediaResourceId = mediaItem?.resourceId
      const getMediaDetailsFromListToShow = this.$utils.getItemFromList(this.mediaList.itemsToShow, 'resourceId', targetMediaResourceId)
      const getMediaDetailsFromSelection = this.$utils.getItemFromList(this.mediaSelection, 'resourceId', targetMediaResourceId)
      const mediaDetailsFromItemsToShow = getMediaDetailsFromListToShow.value

      if (tagVal === CONST.ALL_TAG_VALUE) {
        return
      }

      // PopId should not be added in any audio files
      if (popIdVal && ['audio', 'audio-playlist'].includes(getMediaDetailsFromListToShow.value.type)) {
        return
      }

      let updateMediaListing = false

      if (tagVal && !mediaDetailsFromItemsToShow.tags.includes(tagVal)) { // Tag
        mediaDetailsFromItemsToShow.tags.push(tagVal)
        updateMediaListing = true
      } else if (popIdVal && mediaDetailsFromItemsToShow.popId !== popIdVal) { // PopId
        mediaDetailsFromItemsToShow.popId = popIdVal
        updateMediaListing = true
      }

      if (updateMediaListing) {
        // Update media list batch history and items to show
        const mediaList = this.$utils.clone(this.mediaList)
        mediaList.batchHistory.forEach((mediaByBatch) => {
          const updatedMediaByBatch = this.$utils.updateItemFromList(mediaByBatch, mediaDetailsFromItemsToShow, 'resourceId')
          mediaByBatch = updatedMediaByBatch
        })
        const newMediaListToShow = this.$utils.updateItemFromList(mediaList.itemsToShow, mediaDetailsFromItemsToShow, 'resourceId')
        mediaList.itemsToShow = newMediaListToShow
        this.mediaList = mediaList

        // Update media in the listing if selected, else send request to the backend with updated data
        if (mediaDetailsFromItemsToShow._edit.selected) {
          const mediaDetailsFromSelection = getMediaDetailsFromSelection.value

          mediaDetailsFromSelection.tags = mediaDetailsFromItemsToShow.tags
          mediaDetailsFromSelection.popId = mediaDetailsFromItemsToShow.popId

          const mediaSelection = this.$utils.clone(this.mediaSelection)
          mediaSelection[getMediaDetailsFromSelection.position] = mediaDetailsFromSelection
          this.mediaSelection = mediaSelection
          this.mediaMenuTriggerPropertyAction = {
            action: 'mediaAttributeValueChanged',
            attributeName: 'tags',
            refNum: this.$utils.generateUniqueID()
          }
        } else {
          const requestPayload = this.$utils.clone(mediaDetailsFromItemsToShow)

          delete requestPayload._edit
          delete requestPayload.status

          const apiRequest = () => {
            this.$utils.mainLoader(true)
            this.$api.put(`${CONFIG.API_BASE_URL}/media/${targetMediaResourceId}?use_qsa=1`, requestPayload)
              .then(response => {
                this.handleMediaCardSelection({}, mediaDetailsFromItemsToShow, getMediaDetailsFromListToShow.position)
                this.$utils.mainLoader(false)
              })
              .catch((error) => {
                this.$utils.mainLoader(false)
                this.$apiError.handleAPIRequestError(error, { originalRequest: () => { apiRequest() }, showDialogNotification: true })
              })
          }
          apiRequest()
        }
      }
    }
  },
  beforeUnmount () {
    this.removeMessageListener()

    if (document.getElementById(MEDIA_COPY_PASTE_TEXTAREA_ID)) {
      document.body.removeChild(document.getElementById(MEDIA_COPY_PASTE_TEXTAREA_ID))
    }
  },
  mounted () {
    this.setDefaultData({ prevAction: 'componentMounted' })
  }
}
